import { useState, useEffect, useCallback } from 'react'
import { UserHiddenColumnsService } from '../services/user-hidden-columns.service'

export interface ColumnVisibility {
  hideSerialNumber: boolean
  hideYearMfr: boolean
  hideAftt: boolean
  hideOwner: boolean
  hideBroker: boolean
  hideEmp: boolean
  hideDmg: boolean
  hidePax: boolean
  hidePaintInterior: boolean
  hideOptions: boolean
  hideDateListed: boolean
  hideJnAsk: boolean
  hideAskingPrice: boolean
  hideExternalColumns: boolean
  hideRelativeValue: boolean
}

export function useMarketColumns() {
  // Hidden columns state - stored per-user per-model in database
  const [hiddenColumns, setHiddenColumns] = useState<Set<string>>(new Set())
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_hiddenColumnsLoaded, setHiddenColumnsLoaded] = useState(false)
  // Cell borders visibility state
  const [showCellBorders, setShowCellBorders] = useState(true)
  // Historical data range for filtering sold/removed tables (in months)
  const [historicalMonths, setHistoricalMonths] = useState(UserHiddenColumnsService.DEFAULT_HISTORICAL_MONTHS)

  // Derive hide flags from hiddenColumns set to pass to MarketTable
  const columnVisibility: ColumnVisibility = {
    hideSerialNumber: hiddenColumns.has('serialNumber'),
    hideYearMfr: hiddenColumns.has('yearMfr'),
    hideAftt: hiddenColumns.has('aftt'),
    hideOwner: hiddenColumns.has('owner'),
    hideBroker: hiddenColumns.has('broker'),
    hideEmp: hiddenColumns.has('emp'),
    hideDmg: hiddenColumns.has('dmg'),
    hidePax: hiddenColumns.has('pax'),
    hidePaintInterior: hiddenColumns.has('paintInterior'),
    hideOptions: hiddenColumns.has('options'),
    hideDateListed: hiddenColumns.has('dateListed'),
    hideJnAsk: hiddenColumns.has('jnAsk'),
    hideAskingPrice: hiddenColumns.has('askingPrice'),
    hideExternalColumns: hiddenColumns.has('externalColumns'),
    hideRelativeValue: hiddenColumns.has('relativeValue'),
  }

  // Load hidden columns, cell borders preference, and historical months from database once on mount
  useEffect(() => {
    const loadUserPreferences = async () => {
      try {
        const hidden = await UserHiddenColumnsService.getForCurrentUser()
        // Filter out special markers from hidden columns for the column visibility
        const columnHidden = hidden.filter(id =>
          id !== UserHiddenColumnsService.CELL_BORDERS_ID &&
          !id.startsWith(UserHiddenColumnsService.HISTORICAL_MONTHS_PREFIX)
        )
        setHiddenColumns(new Set(columnHidden))
        // Check if cell borders are hidden
        const cellBordersHidden = hidden.includes(UserHiddenColumnsService.CELL_BORDERS_ID)
        setShowCellBorders(!cellBordersHidden)
        // Load historical months preference
        const months = UserHiddenColumnsService.getHistoricalMonthsFromArray(hidden)
        setHistoricalMonths(months)
      } catch (error) {
        // Error loading hidden columns - silently ignore
      } finally {
        setHiddenColumnsLoaded(true)
      }
    }
    loadUserPreferences()
  }, []) // Empty dependency - load once on mount

  // Handle toggling column visibility - update immediately, save in background
  const handleToggleColumn = useCallback((columnId: string) => {
    setHiddenColumns(prev => {
      const newHidden = new Set(prev)
      if (newHidden.has(columnId)) {
        newHidden.delete(columnId)
      } else {
        newHidden.add(columnId)
      }
      // Save to database in background (don't await)
      // Include cell borders marker if borders are hidden
      const columnsToSave = Array.from(newHidden)
      if (!showCellBorders) {
        columnsToSave.push(UserHiddenColumnsService.CELL_BORDERS_ID)
      }
      UserHiddenColumnsService.save(columnsToSave).catch(() => {
        // Error saving hidden columns
      })
      return newHidden
    })
  }, [showCellBorders])

  // Handle reset to defaults (show all columns) - update immediately, save in background
  const handleResetToDefaults = useCallback(() => {
    setHiddenColumns(new Set())
    // Save to database in background (don't await)
    UserHiddenColumnsService.resetToDefaults().catch(() => {
      // Error resetting hidden columns
    })
  }, [])

  // Handle toggling cell borders visibility
  const handleToggleCellBorders = useCallback(() => {
    setShowCellBorders(prev => {
      const newValue = !prev
      // Save to database in background
      UserHiddenColumnsService.setCellBordersVisible(newValue).catch(() => {
        // Error saving cell borders preference
      })
      return newValue
    })
  }, [])

  // Handle historical months change - update immediately, save in background
  const handleHistoricalMonthsChange = useCallback((months: number) => {
    const clampedMonths = Math.max(1, Math.min(48, months))
    setHistoricalMonths(clampedMonths)
    // Save to database in background
    UserHiddenColumnsService.setHistoricalMonths(clampedMonths).catch(() => {
      // Error saving historical months preference
    })
  }, [])

  return {
    hiddenColumns,
    showCellBorders,
    historicalMonths,
    columnVisibility,
    handleToggleColumn,
    handleResetToDefaults,
    handleToggleCellBorders,
    handleHistoricalMonthsChange,
  }
}

